// SIMULATE A SCHEDULE (owner, 2026-10-16: "add a simulate a schedule button
// where you can upload text/excel/pdf/dwg files or select from meeting
// minutes imported, doc control, sheets .. documents and analyze it and
// produce a schedule based also on other available info in technical
// documentation and levels, areas and systems, make it run on the light
// worker or heavy worker and show spinning until schedule is produced").
//
// Three faces of one dialog:
//   PICK     upload files, and/or choose Document Control documents and
//            imported meeting minutes; earlier simulations listed below
//   RUNNING  a spinner with the stage the heavy-lane job is on (polled)
//   RESULT   the simulated schedule (bars by stage > level > area), the
//            changes against the schedule as it stands - each ticked or
//            not, removals and re-opens never ticked for you - the work
//            packages found with where each was placed and from which page,
//            and the questions the documents leave open. Apply writes only
//            the ticked changes; Undo takes them back.
// Nothing reaches the schedule before Apply (back/utils/scheduleSimulation).
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Collapse, Empty, InputNumber, Modal, Popconfirm, Select, Space, Spin, Table, Tabs, Tag, Tooltip, Upload, message } from "antd";
import { InboxOutlined } from "@ant-design/icons";
import {
  listSimulationSources, listSimulations, getSimulation, startSimulation,
  applySimulation, undoSimulation, retrySimulation, dismissSimulation,
} from "api/scheduleSimulation/scheduleSimulationApi";
import { matchOption } from "components/SearchSelect/searchSelect";

const ACCEPT = ".pdf,.docx,.xlsx,.xlsm,.xls,.csv,.tsv,.txt,.md,.dwg";
const KIND = {
  create: { label: "New row", color: "green" },
  redate: { label: "New dates", color: "blue" },
  signoff: { label: "Sign-off", color: "purple" },
  reopen: { label: "Re-open", color: "orange" },
  remove: { label: "Remove", color: "red" },
  blocked: { label: "Cannot place", color: "default" },
};
const STATUS = { signed: "Signed off", submitted: "Submitted", reviewed: "Reviewed", open: "Open", overdue: "Overdue", not_started: "Not started", tbd: "To be released" };
const fmt = (v) => (v ? new Date(`${String(v).slice(0, 10)}T00:00:00Z`).toLocaleDateString(undefined, { timeZone: "UTC", month: "short", day: "numeric", year: "2-digit" }) : "—");
const POLL_MS = 2500;

// The simulated schedule as bars on one shared time axis.
export function SimulatedBars({ rows }) {
  const list = (rows || []).filter((r) => r.start && r.end);
  if (!list.length) return <Empty description="Nothing dated to draw" />;
  const t = (v) => Date.parse(`${v}T00:00:00Z`);
  const lo = Math.min(...list.map((r) => t(r.start)));
  const hi = Math.max(...list.map((r) => t(r.end)));
  const span = Math.max(hi - lo, 86400000);
  const groups = new Map();
  for (const r of list) {
    const k = `${r.stage} › ${r.level}`;
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k).push(r);
  }
  return (
    <div data-testid="sim-bars" style={{ fontSize: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", color: "#888", marginLeft: 280, marginBottom: 4 }}>
        <span>{fmt(new Date(lo).toISOString())}</span><span>{fmt(new Date(hi).toISOString())}</span>
      </div>
      {Array.from(groups.entries()).map(([g, rs]) => (
        <div key={g} style={{ marginBottom: 10 }}>
          <div style={{ fontWeight: 600, marginBottom: 2 }}>{g}</div>
          {rs.sort((a, b) => String(a.start).localeCompare(String(b.start))).map((r, i) => {
            const left = ((t(r.start) - lo) / span) * 100;
            const width = Math.max(((t(r.end) - t(r.start)) / span) * 100, 0.8);
            return (
              <div key={`${r.name}-${i}`} style={{ display: "flex", alignItems: "center", height: 20 }}>
                <span title={r.name} style={{ width: 280, flexShrink: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", paddingRight: 8 }}>{r.name}</span>
                <div style={{ position: "relative", flex: 1, height: 12, background: "#f5f5f5", borderRadius: 2 }}>
                  <Tooltip title={`${fmt(r.start)} - ${fmt(r.end)}${r.signedOff ? ` · signed off ${fmt(r.signedOff)}` : ""}${r.src && r.src.file ? ` · ${r.src.file} p.${r.src.page}` : ""}`}>
                    <div style={{ position: "absolute", left: `${left}%`, width: `${width}%`, top: 0, bottom: 0, borderRadius: 2, background: r.signedOff ? "#52c41a" : "#1677ff", opacity: r.existing ? 1 : 0.75 }} />
                  </Tooltip>
                </div>
              </div>
            );
          })}
        </div>
      ))}
      <div style={{ color: "#888" }}><span style={{ display: "inline-block", width: 10, height: 10, background: "#52c41a", marginRight: 4 }} />signed off <span style={{ display: "inline-block", width: 10, height: 10, background: "#1677ff", margin: "0 4px 0 12px" }} />planned</div>
    </div>
  );
}

export default function ScheduleSimulator({ projectId, open, onClose, onApplied }) {
  const [sources, setSources] = useState({ documents: [], minutes: [] });
  const [history, setHistory] = useState([]);
  const [files, setFiles] = useState([]);
  const [docIds, setDocIds] = useState([]);
  const [minuteIds, setMinuteIds] = useState([]);
  const [options, setOptions] = useState({ reforecastDays: 10, packageDays: 10, installDays: 15 });
  const [sim, setSim] = useState(null);
  const [can, setCan] = useState({ apply: false, remove: false });
  const [ticked, setTicked] = useState([]);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  const loadLists = useCallback(() => {
    if (!projectId) return;
    listSimulationSources(projectId).then(setSources).catch(() => setSources({ documents: [], minutes: [] }));
    listSimulations(projectId).then((r) => setHistory(r.simulations || [])).catch(() => setHistory([]));
  }, [projectId]);
  useEffect(() => { if (open) loadLists(); }, [open, loadLists]);
  useEffect(() => () => clearTimeout(timer.current), []);

  const show = useCallback(async (id) => {
    clearTimeout(timer.current);
    try {
      const r = await getSimulation(projectId, id);
      setSim(r.simulation);
      setCan(r.can || {});
      if (r.simulation.status === "ready" && r.simulation.result) {
        setTicked((r.simulation.result.changes || []).filter((c) => c.tick).map((c) => c.id));
      }
      if (["queued", "reading", "applying"].includes(r.simulation.status)) timer.current = setTimeout(() => show(id), POLL_MS);
    } catch (e) { message.error(e.message || "Could not read the simulation"); }
  }, [projectId]);

  const start = async () => {
    setBusy(true);
    try {
      const r = await startSimulation(projectId, { files, documents: docIds, minutes: minuteIds, options });
      if (r.rejected && r.rejected.length) message.warning(`Left out (not a readable type): ${r.rejected.join(", ")}`);
      setFiles([]); setDocIds([]); setMinuteIds([]);
      show(r.simulation._id);
    } catch (e) { message.error(e.message || "Could not start"); } finally { setBusy(false); }
  };
  const apply = async () => {
    setBusy(true);
    try {
      const r = await applySimulation(projectId, sim._id, ticked);
      const c = r.applied.counts;
      message.success(`Applied: ${c.created} new, ${c.updated} changed, ${c.removed} removed - Undo is on this page`);
      await show(sim._id);
      if (onApplied) onApplied();
    } catch (e) { message.error(e.message || "Could not apply"); } finally { setBusy(false); }
  };
  const undo = async (force = false) => {
    setBusy(true);
    try {
      await undoSimulation(projectId, sim._id, force);
      message.success("Undone - the schedule is as it was before");
      await show(sim._id);
      if (onApplied) onApplied();
    } catch (e) {
      if (e.code === "edited_since") {
        Modal.confirm({ title: "Rows were edited after the apply", content: `${e.message} Undo anyway?`, okText: "Undo anyway", onOk: () => undo(true) });
      } else message.error(e.message || "Could not undo");
    } finally { setBusy(false); }
  };

  const result = sim && sim.result;
  const running = sim && ["queued", "reading"].includes(sim.status);
  const changeCols = [
    { title: "Change", dataIndex: "kind", width: 110, render: (k) => <Tag color={KIND[k].color}>{KIND[k].label}</Tag>,
      filters: Object.entries(KIND).map(([value, v]) => ({ text: v.label, value })), onFilter: (v, r) => r.kind === v },
    { title: "Row", render: (c) => (
      <div>
        <div>{c.row.existing ? c.row.existing.name : c.row.name}</div>
        {c.row.existing && c.row.existing.name !== c.row.name ? <div style={{ fontSize: 11, color: "#888" }}>from the documents: {c.row.package}</div> : null}
        <div style={{ fontSize: 11, color: "#888" }}>{c.row.stage} › {c.row.level} › {c.row.area}{c.row.line ? ` · on ${c.row.line}` : ""}</div>
      </div>
    ) },
    { title: "What changes", width: 300, render: (c) => {
      if (c.kind === "create") return `${fmt(c.row.start)} - ${fmt(c.row.end)}${c.row.signedOff ? `, signed off ${fmt(c.row.signedOff)}` : ""}`;
      if (c.kind === "redate") return `${fmt(c.from.start)} - ${fmt(c.from.end)} → ${fmt(c.to.start)} - ${fmt(c.to.end)}`;
      if (c.kind === "signoff") return `${c.from ? `signed ${fmt(c.from)}` : "not signed"} → signed ${fmt(c.to)}`;
      return c.reason || "";
    } },
    { title: "Source", width: 170, render: (c) => (c.row.src && c.row.src.file ? <Tooltip title={c.row.src.snippet}><span style={{ fontSize: 12 }}>{c.row.src.file} p.{c.row.src.page}</span></Tooltip> : "") },
  ];
  const pkgCols = [
    { title: "Work package", render: (p) => <div><b>{p.label}</b>{p.notes ? <div style={{ fontSize: 11, color: "#888" }}>{p.notes.slice(0, 160)}</div> : null}</div> },
    { title: "Status", width: 110, render: (p) => STATUS[p.status] || p.status },
    { title: "Placed on", width: 230, render: (p) => (p.where ? (
      <div style={{ fontSize: 12 }}>
        {p.where.levels.join(", ") || "no level"} · {p.where.lines.length ? p.where.lines.join(", ") : "no line"} · areas {p.where.areas.join(", ") || "none"}
        {p.where.note ? <div style={{ color: "#b45309" }}>{p.where.note}</div> : null}
        {p.need ? <div style={{ color: "#b45309" }}>Needs {p.need}</div> : null}
      </div>
    ) : null) },
    { title: "Dates", width: 230, render: (p) => <span style={{ fontSize: 12 }}>{p.milestones || (p.drawn === false ? "no date" : "")}{p.reforecast ? <div style={{ color: "#b45309" }}>re-forecast to {fmt(p.end)}</div> : null}</span> },
    { title: "Source", width: 170, render: (p) => (p.src ? <Tooltip title={p.src.snippet}><span style={{ fontSize: 12 }}>{p.src.file} p.{p.src.page}</span></Tooltip> : "") },
  ];
  const pkgRows = useMemo(() => (result ? [...(result.placed || []), ...(result.unplaced || [])].map((p, i) => ({ ...p, key: i })) : []), [result]);

  const pickFace = (
    <div>
      <Upload.Dragger multiple accept={ACCEPT} fileList={files.map((f, i) => ({ uid: String(i), name: f.name, status: "done" }))}
        beforeUpload={(f) => { setFiles((x) => [...x, f]); return false; }}
        onRemove={(f) => setFiles((x) => x.filter((_, i) => String(i) !== f.uid))} data-testid="sim-upload">
        <p className="ant-upload-drag-icon"><InboxOutlined /></p>
        <p>Drop sign-off registers, trackers, look-aheads, meeting minutes or drawings here</p>
        <p style={{ color: "#888", fontSize: 12 }}>PDF, Word, Excel, CSV, text or DWG - each one is filed in Document Control</p>
      </Upload.Dragger>
      <div style={{ marginTop: 12, fontWeight: 500 }}>Or pick what the project already has</div>
      <Select filterOption={matchOption} mode="multiple" allowClear showSearch optionFilterProp="label" style={{ width: "100%", marginTop: 6 }} aria-label="documents"
        placeholder={`Document Control - ${sources.documents.length} documents (sheets, minutes, registers, drawings)`} value={docIds} onChange={setDocIds}
        options={sources.documents.map((d) => ({ value: String(d._id), label: `${d.name}${d.kind ? ` · ${d.kind.toUpperCase()}` : ""}${d.docType && d.docType !== "sheet" ? ` · ${d.docType}` : ""}` }))} />
      <Select filterOption={matchOption} mode="multiple" allowClear showSearch optionFilterProp="label" style={{ width: "100%", marginTop: 6 }} aria-label="minutes"
        placeholder={`Imported meeting minutes - ${sources.minutes.length} meetings`} value={minuteIds} onChange={setMinuteIds}
        options={sources.minutes.map((m) => ({ value: String(m._id), label: `${m.series} · ${fmt(m.date)} · ${m.items} items` }))} />
      <div style={{ fontSize: 12, color: "#888", marginTop: 8 }}>
        Always read as well: the levels, areas, lines and systems on Levels, Areas &amp; Systems, the technical documentation, and the schedule as it stands.
      </div>
      <Collapse ghost size="small" style={{ marginTop: 4 }} items={[{ key: "o", label: "Options", children: (
        <Space wrap>
          <span>Re-forecast an overdue package <InputNumber min={1} max={120} value={options.reforecastDays} onChange={(v) => setOptions((o) => ({ ...o, reforecastDays: v || 10 }))} /> working days from today</span>
          <span>A package with only an end takes <InputNumber min={1} max={120} value={options.packageDays} onChange={(v) => setOptions((o) => ({ ...o, packageDays: v || 10 }))} /> working days</span>
          <span>Mains install <InputNumber min={1} max={250} value={options.installDays} onChange={(v) => setOptions((o) => ({ ...o, installDays: v || 15 }))} /> working days</span>
        </Space>
      ) }]} />
      <Button type="primary" style={{ marginTop: 8 }} loading={busy} disabled={!files.length && !docIds.length && !minuteIds.length} onClick={start} data-testid="sim-start">
        Simulate
      </Button>
      {history.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontWeight: 500, marginBottom: 4 }}>Earlier simulations</div>
          {history.map((h) => (
            <div key={h._id} style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 12, padding: "2px 0" }}>
              <Button size="small" type="link" style={{ padding: 0 }} onClick={() => show(h._id)}>{new Date(h.createdAt).toLocaleString()}</Button>
              <Tag>{h.status}</Tag>
              <span style={{ color: "#888" }}>{(h.sources || []).map((s) => s.name).join(", ").slice(0, 120)}</span>
              {h.counts ? <span style={{ color: "#888" }}>{h.counts.packages} packages · {h.counts.create + h.counts.redate + h.counts.signoff} changes</span> : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const runningFace = sim && (
    <div style={{ textAlign: "center", padding: "48px 0" }} data-testid="sim-running">
      <Spin size="large" />
      <div style={{ marginTop: 16, fontSize: 15 }}>{sim.progress && sim.progress.stage ? sim.progress.stage : "Working"}</div>
      {sim.progress && sim.progress.total ? <div style={{ color: "#888", marginTop: 4 }}>{Math.min(sim.progress.done + 1, sim.progress.total)} of {sim.progress.total} source{sim.progress.total === 1 ? "" : "s"}</div> : null}
      <div style={{ color: "#888", marginTop: 12, fontSize: 12 }}>It runs on the server - you can close this and come back; it is listed under Earlier simulations.</div>
    </div>
  );

  const resultFace = sim && (
    <div data-testid="sim-result">
      {sim.status === "failed" && <Alert type="error" showIcon message="The simulation stopped" description={sim.error} style={{ marginBottom: 12 }} />}
      {sim.extraction && sim.extraction.note ? <Alert type="warning" showIcon message={sim.extraction.note} style={{ marginBottom: 12 }} /> : null}
      {(sim.sources || []).filter((s) => s.note).map((s) => <div key={s.name} style={{ fontSize: 12, color: "#888" }}>{s.name}: {s.note}</div>)}
      {sim.applied && (
        <Alert type="success" showIcon style={{ margin: "8px 0" }}
          message={`Applied ${new Date(sim.applied.at).toLocaleString()} by ${sim.applied.byName}: ${sim.applied.counts.created} new, ${sim.applied.counts.updated} changed, ${sim.applied.counts.removed} removed`}
          action={<Button size="small" danger loading={busy} onClick={() => undo(false)}>Undo</Button>} />
      )}
      {result && (
        <>
          <Space wrap style={{ margin: "8px 0" }}>
            <Tag>{result.counts.packages} work packages</Tag>
            <Tag color="green">{result.counts.placed} placed</Tag>
            {result.counts.unplaced ? <Tag color="orange">{result.counts.unplaced} not placed</Tag> : null}
            {Object.keys(KIND).map((k) => (result.counts[k] ? <Tag key={k} color={KIND[k].color}>{result.counts[k]} {KIND[k].label.toLowerCase()}</Tag> : null))}
            {(result.questions || []).length ? <Tag color="gold">{result.questions.length} questions</Tag> : null}
          </Space>
          <Tabs items={[
            { key: "changes", label: `Changes (${(result.changes || []).length})`, children: (
              <Table size="small" rowKey="id" columns={changeCols} dataSource={result.changes || []} pagination={{ pageSize: 50 }}
                rowSelection={sim.status === "ready" ? {
                  selectedRowKeys: ticked, onChange: (k) => setTicked(k),
                  getCheckboxProps: (c) => ({ disabled: c.kind === "blocked" || (c.kind === "remove" && !can.remove) }),
                } : undefined} />
            ) },
            { key: "bars", label: "Simulated schedule", children: <SimulatedBars rows={result.proposal} /> },
            { key: "packages", label: `Work packages (${pkgRows.length})`, children: <Table size="small" columns={pkgCols} dataSource={pkgRows} pagination={{ pageSize: 50 }} /> },
            { key: "questions", label: `Questions (${(result.questions || []).length})`, children: (
              (result.questions || []).length ? (
                <ul>{result.questions.map((q, i) => <li key={i}><b>{q.package}</b>: {q.text}</li>)}</ul>
              ) : <Empty description="The documents leave nothing open" />
            ) },
            { key: "context", label: "What it read", children: result.context ? (
              <div style={{ fontSize: 12 }}>
                <div><b>Levels:</b> {result.context.levels.join(", ") || "none"}</div>
                <div><b>Areas:</b> {result.context.areas.join(", ") || "none"}</div>
                <div><b>Lines:</b> {result.context.lines.map((l) => `${l.name}${l.level ? ` (${l.level})` : ""}${l.areas.length ? ` in ${l.areas.join("/")}` : " - no areas"}`).join("; ") || "none"}</div>
                <div><b>Systems:</b> {result.context.systems.join(", ") || "none"}</div>
                <div><b>Technical documentation:</b> {result.context.technicalDocuments.join(", ") || "none"}</div>
              </div>
            ) : null },
          ]} />
        </>
      )}
      <Space style={{ marginTop: 12 }}>
        {sim.status === "ready" && result && (
          <Popconfirm title={`Apply ${ticked.length} change${ticked.length === 1 ? "" : "s"} to the schedule?`} description="Only the ticked rows are written. Undo stays on this page." onConfirm={apply} disabled={!ticked.length || !can.apply}>
            <Button type="primary" loading={busy} disabled={!ticked.length || !can.apply} data-testid="sim-apply"
              title={can.apply ? "" : "Needs the ganttAdd and ganttEdit permissions"}>
              Apply {ticked.length} ticked change{ticked.length === 1 ? "" : "s"}
            </Button>
          </Popconfirm>
        )}
        {["ready", "failed"].includes(sim.status) && <Button loading={busy} onClick={async () => { try { await retrySimulation(projectId, sim._id); show(sim._id); } catch (e) { message.error(e.message); } }}>Run again</Button>}
        {["ready", "failed"].includes(sim.status) && <Button onClick={async () => { try { await dismissSimulation(projectId, sim._id); setSim(null); loadLists(); } catch (e) { message.error(e.message); } }}>Dismiss</Button>}
        <Button onClick={() => { clearTimeout(timer.current); setSim(null); loadLists(); }}>New simulation</Button>
      </Space>
    </div>
  );

  return (
    <Modal open={open} onCancel={() => { clearTimeout(timer.current); onClose(); }} footer={null} width={1150} destroyOnClose
      title="Simulate a schedule from documents">
      {!sim ? pickFace : (running ? runningFace : resultFace)}
    </Modal>
  );
}
